import { useMemo } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useSetRecoilState } from "recoil";

import { useYupValidationResolver } from "@ryde/redux";
import {
  IApiError,
  IDropdownItemStations,
  Log,
  normalizeErrors,
} from "@ryde/shared";
import {
  apiDataFetchingLandingAtom,
  ChooseStationStepFormFieldsEnum,
  createMessage,
  IChooseStationLandingModel,
  landingDropdownStationListAtom,
  landingMessagesAtom,
  LandingStepEnum,
  useLandingActions,
  useLandingApiStations,
  useLandingsAuth,
} from "@ryde/utils-landings";

import { yupChooseStationLandingValidationSchema } from "./parts";

export const useStepChooseStation = () => {
  const { goToStep } = useLandingActions();
  const { getOfficialStationsByHomeAddress, setPassengerPickupStation } =
    useLandingApiStations();
  const setStationsDropdownList = useSetRecoilState(
    landingDropdownStationListAtom,
  );
  const { t } = useTranslation();

  const setServerErrors = useSetRecoilState(landingMessagesAtom);
  const setApiLoading = useSetRecoilState(apiDataFetchingLandingAtom);
  const { handleLogout } = useLandingsAuth();

  const resolver = useYupValidationResolver(
    yupChooseStationLandingValidationSchema,
    "landing",
  );

  const methods = useForm<IChooseStationLandingModel>({
    mode: "all",
    reValidateMode: "onChange",
    criteriaMode: "firstError",
    defaultValues: {
      homeAddress: {
        fullAddress: "",
        longitude: undefined,
        latitude: undefined,
      },
      sourceStation2Id: undefined,
      acceptTerms: false,
    },
    resolver,
  });

  const {
    getValues,
    clearErrors,
    setError,
    setValue,
    trigger,
    watch,
    formState: { isValid },
  } = methods;

  const homeAddressWatchValue = watch(
    ChooseStationStepFormFieldsEnum.HomeAddress,
  );
  const sourceStation2IdWatchValue = watch(
    ChooseStationStepFormFieldsEnum.SourceStation2Id,
  );
  const acceptTermsWatchValue = watch(
    ChooseStationStepFormFieldsEnum.AcceptTerms,
  );

  const handleHomeAddressChange = useEvent(async (): Promise<void> => {
    setServerErrors(undefined);
    const homeAddress = getValues(ChooseStationStepFormFieldsEnum.HomeAddress);

    if (!homeAddress.latitude || !homeAddress.longitude) {
      setError(ChooseStationStepFormFieldsEnum.HomeAddress, {
        message: t("errors.missingCoordinates"),
      });
      setValue(ChooseStationStepFormFieldsEnum.SourceStation2Id, undefined);
      setStationsDropdownList([]);
      return;
    }

    try {
      const response = await getOfficialStationsByHomeAddress({ homeAddress });

      const { success, data } = response ?? {};

      if (success && data) {
        if (
          "isHomeAddressInPermittedPolygon" in data &&
          !data.isHomeAddressInPermittedPolygon
        ) {
          setError(ChooseStationStepFormFieldsEnum.HomeAddress, {
            message: t("errors.notInPermittedPolygon"),
          });
        }

        if (data.officialStationsList?.length) {
          setStationsDropdownList(
            data.officialStationsList as IDropdownItemStations[],
          );
        }

        setValue(ChooseStationStepFormFieldsEnum.SourceStation2Id, undefined);
        clearErrors(ChooseStationStepFormFieldsEnum.SourceStation2Id);

        if (data.selectedStationId) {
          setValue(
            ChooseStationStepFormFieldsEnum.SourceStation2Id,
            data.selectedStationId,
            {
              shouldDirty: true,
              shouldValidate: true,
            },
          );
        } else {
          setError(ChooseStationStepFormFieldsEnum.SourceStation2Id, {
            message: t("errors.noNearbyStationFoundMessage"),
          });
        }
      }
    } catch (error) {
      const errorsNormalized = normalizeErrors(error as IApiError, t);
      setServerErrors(createMessage(errorsNormalized));
      Log.warnAndToast(errorsNormalized, error);
    }
  });

  const handleChooseStation = useEvent(async (): Promise<void> => {
    const isValidAfterTrigger = await trigger();

    if (!isValidAfterTrigger) {
      return;
    }

    const homeAddress = getValues(ChooseStationStepFormFieldsEnum.HomeAddress);
    const sourceStation2Id = getValues(
      ChooseStationStepFormFieldsEnum.SourceStation2Id,
    );
    const acceptTerms = getValues(ChooseStationStepFormFieldsEnum.AcceptTerms);

    if (sourceStation2Id && homeAddress && acceptTerms) {
      setApiLoading(true);

      try {
        const response = await setPassengerPickupStation({
          sourceStation2Id,
        });

        const { success, data } = response ?? {};

        if (success && data) {
          setServerErrors(createMessage([]));
          goToStep(LandingStepEnum.FinishedStep);
          handleLogout();
          return;
        }
      } catch (error) {
        Log.error(
          "failed update passenger homeStation and sourceStation2Id. error:",
          error,
        );
      } finally {
        setApiLoading(false);
      }

      setServerErrors(createMessage(["errors.updateNotSuccess"]));
    }
  });

  const isContinueCanBeApply = useMemo(() => {
    return (
      isValid &&
      homeAddressWatchValue.fullAddress &&
      homeAddressWatchValue.latitude &&
      homeAddressWatchValue.longitude &&
      sourceStation2IdWatchValue &&
      acceptTermsWatchValue
    );
  }, [
    acceptTermsWatchValue,
    homeAddressWatchValue.fullAddress,
    homeAddressWatchValue.latitude,
    homeAddressWatchValue.longitude,
    isValid,
    sourceStation2IdWatchValue,
  ]);

  return {
    methods,
    handleChooseStation,
    handleHomeAddressChange,
    isContinueCanBeApply,
  };
};
